<template>
  <UModal
    v-model:open="open"
    :title="t('dialog.cookie.title')"
    :description="t('dialog.cookie.desc')"
    :ui="{
      body: 'flex flex-col gap-2',
    }"
  >
    <template #body>
      <div class="flex justify-end items-center flex-wrap">
        <UButton
          v-if="storageCkSetting.init"
          icon="mdi-refresh"
          variant="ghost"
          size="sm"
          color="neutral"
          @click="clearAll()"
        />
        <UButton
          variant="ghost"
          size="sm"
          color="neutral"
          :disabled="isOnlyNecessarySelected"
          @click="toggleAll(false)"
        >
          {{ t('dialog.cookie.allowNecessary') }}
        </UButton>
        <UButton
          variant="ghost"
          size="sm"
          color="neutral"
          :disabled="isAllSelected"
          @click="toggleAll(true)"
        >
          {{ t('dialog.cookie.allowAll') }}
        </UButton>
      </div>

      <div class="space-y-3">
        <div
          v-for="item in cookieOptions"
          :key="item.key"
          class="flex items-start justify-between gap-4 p-3 border border-default bg-default/50"
        >
          <div class="flex items-start gap-3">
            <UIcon :name="item.icon" :class="[item.iconColor, 'size-4 mt-0.5 shrink-0']" />
            <div>
              <p class="text-sm font-semibold text-default">{{ t(item.title) }}</p>
              <p class="text-xs text-muted mt-0.5 leading-relaxed">{{ t(item.desc) }}</p>
            </div>
          </div>

          <USwitch
            v-model="draft[item.key]"
            :disabled="item.key === 'basic'"
            color="success"
            class="mt-0.5"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end gap-2 w-full">
        <UButton variant="solid" color="primary" leading-icon="mdi-check" @click="handleConfirm">
          {{ t('action.save') }}
        </UButton>
        <UButton variant="outline" leading-icon="mdi-close" color="neutral" @click="closeModal()">
          {{ t('action.cancel') }}
        </UButton>
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
  import { useAppStorage } from '~/config/useConfigStorage'
  import { useCookieModal } from '~/stores/useCookieDialog'
  import type { CkSetting } from '~/types/common/cookie'

  interface CookieOption {
    key: keyof CkSetting
    icon: string
    iconColor: string
    title: string
    desc: string
  }

  const { storageCkSetting } = useAppStorage()
  const { t } = useI18n()
  const { open, closeModal } = useCookieModal()

  /** 临时响应式草稿 */
  const draft = reactive<CkSetting>(storageCkSetting.value)

  /** 开关列表配置 */
  const cookieOptions: CookieOption[] = [
    {
      key: 'basic',
      icon: 'i-lucide-lock-keyhole',
      iconColor: 'text-success',
      title: t('dialog.cookie.basic.title'),
      desc: t('dialog.cookie.basic.desc'),
    },
    {
      key: 'analytics',
      icon: 'i-lucide-chart-bar',
      iconColor: 'text-primary',
      title: t('dialog.cookie.analytics.title'),
      desc: t('dialog.cookie.analytics.desc'),
    },
    {
      key: 'preference',
      icon: 'i-lucide-shield-cog',
      iconColor: 'text-warning',
      title: t('dialog.cookie.preference.title'),
      desc: t('dialog.cookie.preference.desc'),
    },
  ]

  /** 是否已经全部勾选 */
  const isAllSelected = computed(() => {
    return draft.analytics && draft.preference
  })

  /** 是否已经是“仅必要” */
  const isOnlyNecessarySelected = computed(() => {
    return !draft.analytics && !draft.preference
  })

  /** 全选 或 取消全选（仅留必要） */
  function toggleAll(checked: boolean) {
    draft.analytics = checked
    draft.preference = checked
  }

  /** 清空所有设置 */
  function clearAll() {
    if (confirm(t('message.confirm.clearInit'))) {
      storageCkSetting.value.init = false
      storageCkSetting.value.basic = true
      storageCkSetting.value.analytics = false
      storageCkSetting.value.preference = false
      window.location.reload()
    }
  }

  /** 确认：批量写入 storage 并关闭 */
  function handleConfirm() {
    storageCkSetting.value = draft
    closeModal()
  }

  // 打开弹窗时，从当前的存储数据中同步到草稿
  watch(open, val => {
    if (val) {
      // 强制 basic 始终为 true
      draft.basic = true
      draft.analytics = !!storageCkSetting.value.analytics
      draft.preference = !!storageCkSetting.value.preference
    }
  })
</script>
